import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Label } from '../components/ui/label';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import DoctorNoteCard from '../components/DoctorNoteCard';
import ProductCard from '../components/ProductCard';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const quizSteps = [
  {
    id: 'main_goal',
    question: 'What is your main wellness goal right now?',
    options: [
      'Sleeping well', 
      'Desire & sensuality', 
      'Improve focus', 
      'Mindfulness or spiritual calmness', 
      'Gut health', 
      'Respiratory health', 
      'Skin glow'
    ],
    // Map display names to backend category names
    categoryMap: {
      'Sleeping well': 'Sleep',
      'Desire & sensuality': 'Desire',
      'Improve focus': 'Focus',
      'Mindfulness or spiritual calmness': 'Calm',
      'Gut health': 'Gut',
      'Respiratory health': 'Breath',
      'Skin glow': 'Skin'
    }
  },
  {
    id: 'caffeine_sensitivity',
    question: 'How sensitive are you to caffeine?',
    options: ['Very sensitive', 'Moderately sensitive', 'Not sensitive']
  },
  {
    id: 'body_temp',
    question: 'Does your body usually feel cold or hot?',
    subtitle: 'Consider your general body temperature tendency throughout the day',
    options: ['Mostly cold', 'Mostly hot', 'In-between']
  },
  {
    id: 'digestion',
    question: 'How is your digestion?',
    options: ['Often heavy/bloated', 'Mostly fine', 'Often loose/irritated']
  },
  {
    id: 'sleep_pattern',
    question: 'Your typical sleep pattern?',
    options: [
      'Fall asleep easily, wake up often', 
      'Hard to fall asleep', 
      'Irregular schedule',
      'Mostly fine'
    ]
  },
  {
    id: 'medical_conditions',
    question: 'Any medical conditions or allergies we should be aware of?',
    subtitle: 'Select all that apply to ensure safe tea recommendations',
    type: 'multiple',
    options: [
      'Pollen allergy (avoid flower-based teas)',
      'Kidney dysfunction (mineral considerations)',
      'Pregnancy or breastfeeding',
      'Blood pressure concerns',
      'Taking regular medications',
      'None of the above'
    ]
  }
];

export default function Quiz() {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const progress = ((currentStep + 1) / quizSteps.length) * 100;

  const handleAnswer = (questionId, value) => {
    setAnswers({ ...answers, [questionId]: value });
  };

  const handleNext = () => {
    if (currentStep < quizSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      submitQuiz();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const submitQuiz = async () => {
    setLoading(true);
    try {
      const quizData = {
        ...answers,
        flavor_preferences: [],
        extras: []
      };
      
      const response = await axios.post(`${BACKEND_URL}/api/quiz/submit`, quizData, {
        params: { session_id: `session_${Date.now()}` }
      });
      
      setResult(response.data);
      setCurrentStep(quizSteps.length); // Move to result step
    } catch (error) {
      console.error('Error submitting quiz:', error);
      toast.error('Failed to submit quiz. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const currentQuestion = quizSteps[currentStep];
  const canProceed = answers[currentQuestion?.id];

  if (result) {
    return (
      <div className="min-h-screen section-padding bg-soft-ivory" data-testid="quiz-result">
        <div className="max-w-4xl mx-auto space-y-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center space-y-4"
          >
            <h1 className="heading-1">Your Personalized Tea Ritual</h1>
            <p className="body-text text-dark-cedar/70">
              Based on your wellness profile, here are your curated recommendations
            </p>
          </motion.div>

          <DoctorNoteCard note={result.doctor_note} />

          <div className="space-y-6">
            <h2 className="heading-3 text-center">Recommended Products</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {result.recommended_products.map((product, index) => (
                <motion.div
                  key={product.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.2 }}
                >
                  <ProductCard
                    product={product}
                    onAddToCart={() => toast.success(`${product.name} added to cart!`)}
                  />
                </motion.div>
              ))}
            </div>
          </div>

          <div className="flex justify-center space-x-4">
            <Button onClick={() => navigate('/shop')} className="btn-secondary">
              Browse All Products
            </Button>
            <Button onClick={() => { setResult(null); setCurrentStep(0); setAnswers({}); }} className="btn-ghost">
              Retake Quiz
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen section-padding bg-soft-ivory" data-testid="quiz-page">
      <div className="max-w-2xl mx-auto">
        <div className="mb-8">
          <Progress value={progress} className="h-2" />
          <p className="text-sm text-dark-cedar/60 mt-2 text-center">
            Step {currentStep + 1} of {quizSteps.length}
          </p>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="bg-soft-ivory p-8 md:p-12 rounded-lg space-y-8"
          >
            <h2 className="font-playfair text-3xl font-semibold text-dark-cedar">
              {currentQuestion.question}
            </h2>

            <RadioGroup
              value={answers[currentQuestion.id] || ''}
              onValueChange={(value) => handleAnswer(currentQuestion.id, value)}
              className="space-y-4"
            >
              {currentQuestion.options.map((option) => (
                <div key={option} className="flex items-center space-x-3 p-4 rounded-lg border-2 border-dark-cedar/10 hover:border-tiger-orange/30 transition-colors cursor-pointer">
                  <RadioGroupItem value={option} id={option} />
                  <Label htmlFor={option} className="flex-1 cursor-pointer font-inter text-dark-cedar">
                    {option}
                  </Label>
                </div>
              ))}
            </RadioGroup>

            <div className="flex justify-between pt-6">
              <Button
                onClick={handleBack}
                disabled={currentStep === 0}
                variant="outline"
                className="btn-ghost"
                data-testid="quiz-back-button"
              >
                <ChevronLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <Button
                onClick={handleNext}
                disabled={!canProceed || loading}
                className="btn-primary"
                data-testid="quiz-next-button"
              >
                {loading ? 'Submitting...' : (currentStep === quizSteps.length - 1 ? 'Get Results' : 'Next')}
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}